import { ReactElement, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { HttpService } from '../../../../services/HttpService';
import StepsControl from "../../../../components/DocumentGeneration/components/StepsControl/StepsControl";
import { stepsKeys } from "../../../../components/DocumentGeneration/types";

import Terms from './Terms';
import Payment from './Payment';
import Preview from './Preview';
import Completed from './Completed';
import DeliveryService from './DeliveryService';

interface ProseedSubmissionProps {
    companyId: number;
    paymentEnabled: boolean;
}

function ProseedSubmission(props: ProseedSubmissionProps): ReactElement {
    const {
        companyId,
        paymentEnabled,
    } = props;

    const [searchParams] = useSearchParams();
    const documentId = searchParams.get('fileId');
    const [linkExpiry, setLinkExpiry] = useState(0);
    const [isLoading, setLoading] = useState<boolean>(true);
    const [source, setSource] = useState<Record<string, string>>({});
    const [isLastStep, setIsLastStep] = useState<boolean>(Boolean(searchParams.get('rdn')));
    const [step, setStep] = useState(isLastStep ? stepsKeys.completed : stepsKeys.review);

    useEffect(() => {
        if (step === stepsKeys.review) {
            if (Date.now() >= linkExpiry) {
                HttpService.post(
                    `/api/companies/${companyId}/file83b/${documentId}/continue`,
                    {
                        companyId,
                        documentId
                    }
                ).then(({ data }) => {
                    setSource(data);
                    setLinkExpiry(Date.now() + 10000);
                }).finally(() => {
                    setLoading(false);
                })
            }
        }
    }, [companyId, documentId, step, linkExpiry]);

    return (
        <div className="relative overflow-hidden h-full max-h-full">
            <div className="w-full">
                <StepsControl
                    step={step}
                    isNewStyle={true}
                    isPublic={false}
                    paymentEnabled={paymentEnabled}
                    isPublicOpenAi={false}
                    {...(isLastStep ? {
                        isFLowCompleted: true,
                        afterEmbeddedPay: true
                    } : {})}
                />
            </div>
            <div className="w-8 bg-white mx-auto mt-2 pt-4 overflow-hidden" style={{ height: "calc(100% - 91px)", maxHeight: "calc(100% - 91px)" }}>
                {
                    (step === stepsKeys.review && Date.now() < linkExpiry ) ? (
                        <Preview
                            pdfUrl={source?.fileUrl as string}
                            isLoading={isLoading}
                            handleNextStep={() => setStep(stepsKeys.terms)}
                        />
                    ) : null
                }

                {
                    step === stepsKeys.terms && (
                        <Terms
                            isSubmitted={true}
                            handleNextStep={() => setStep(stepsKeys.delivery)}
                            handlePreviousStep={() => setStep(stepsKeys.review)} />
                    )
                }

                {
                    step === stepsKeys.delivery && (
                        <DeliveryService
                            isPaymentEnabled={paymentEnabled}
                            handleNextStep={() => {
                                if (paymentEnabled) {
                                    setStep(stepsKeys.pay);
                                } else {
                                    setStep(stepsKeys.completed);
                                }

                            }}
                            handlePreviousStep={() => setStep(stepsKeys.terms)}
                        />
                    )
                }

                {
                    step === stepsKeys.pay && (
                        <Payment uuid={source.fileUUID}
                            companyId={companyId}
                            handlePreviousStep={() => setStep(stepsKeys.delivery)} />
                    )
                }
                {
                    step === stepsKeys.completed && (
                        <Completed isPaymentEnabled={paymentEnabled} companyId={companyId} documentId={documentId} />
                    )
                }
            </div>
        </div>
    );
}

export default ProseedSubmission;